import { useEffect } from 'react';
import { AppState } from '@/types';
import { getGlobalWeekStart, isNewGlobalWeek } from '@/utils/globalWeekUtils';
import { calculateWeeklyBonus } from '@/lib/weeklyBonusSystem';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useWeekReset = (state: AppState, dispatch: any, setAppState?: any) => {
  const { toast } = useToast();
  
  // Check if week has changed and reset if needed
  useEffect(() => {
    if (!state.isAuthenticated || !state.user?.id) return;
    
    // Use global week boundaries for synchronized resets
    const currentGlobalWeekStart = getGlobalWeekStart();
    const needsReset = isNewGlobalWeek(state.weeklyStartDate);
    
    console.log('🔍 Global week reset check:', {
      currentGlobalWeekStart: currentGlobalWeekStart.toISOString(),
      userWeeklyStartDate: state.weeklyStartDate.toISOString(),
      needsReset,
      currentWeekPoints: state.currentWeekPoints
    });
    
    if (needsReset) {
      console.log('🔄 Weekly reset triggered - saving immediately to database');
      
      // Save weekly leaderboard snapshot before reset (fire and forget)
      supabase.rpc('save_weekly_leaderboard_snapshot')
        .then((result) => {
          if (result.error) {
            console.error('Failed to save weekly leaderboard snapshot:', result.error);
          } else {
            console.log('📸 Weekly leaderboard snapshot saved successfully');
          }
        });
      
      // Get user's baseline goal from state or default to 200
      const baselineGoal = state.user?.weeklyGoal ? 200 : 200; // TODO: fetch from profiles.baseline_goal
      
      // Calculate bonus before resetting
      const bonusResult = calculateWeeklyBonus(
        state.currentWeekPoints,
        state.weeklyGoal,
        baselineGoal
      );
      
      // Show bonus notification if goal was achieved
      if (bonusResult.activityCreditsEarned > 0) {
        toast({
          title: "Weekly Goal Bonus! 🎉",
          description: bonusResult.celebrationMessage,
          duration: 8000,
        });
      }
      
      // Dispatch reset and immediately save to database
      dispatch({ type: 'RESET_WEEK' });
      
      // Force immediate database save of reset state
      if (setAppState) {
        const resetState = {
          ...state,
          currentWeekPoints: 0,
          activitiesLoggedToday: 0,
          weeklyStartDate: currentGlobalWeekStart,
          activityCredits: state.activityCredits + bonusResult.activityCreditsEarned,
          weeklyGoalStreak: bonusResult.activityCreditsEarned > 0 ? state.weeklyGoalStreak + 1 : 0,
          lastWeekGoalAchieved: bonusResult.activityCreditsEarned > 0,
          weeklyBonusEarned: bonusResult.activityCreditsEarned,
          activityCounts: {
            'Closed': 0,
            'Opening': 0,
            'Set': 0,
            'Hangout': 0,
            'Story': 0,
            'Roleplay': 0,
            'Call': 0,
            'Admin': 0,
            'Closing': 0,
            'Referral': 0,
            'Client Servicing': 0
          }
        };
        
        console.log('💾 Force saving weekly reset to database');
        setAppState(resetState);
      }
    }
  }, [dispatch, state.weeklyStartDate, state.isAuthenticated, state.currentWeekPoints, state.weeklyGoal, state.weeklyGoalStreak, state.user?.id, setAppState, toast]);
};